import { useMemo } from 'react';
import dayjs from 'dayjs';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { useWalaLog } from 'components/IAP/hooks/assignmentListHooks';
import {
  ICS204_MUTED,
  ICS204_OUTLINE_BTN,
  ICS204_TABLE,
} from './assignmentCardStyles';
import { mapProposedStatus, ProposedStatusOption } from './assignmentStatus';

interface StatusChangeRow {
  timestamp: string;
  user: string;
  status: ProposedStatusOption;
}

const DIALOG_SURFACE =
  'dark tw:!max-w-6xl tw:sm:!max-w-6xl tw:border-[var(--border-primary)] tw:bg-black tw:text-foreground';

const ChangeLogDialog: React.FC<{
  onClose: () => void;
  walaId: string;
}> = ({ onClose, walaId }) => {
  const { data: activityLog } = useWalaLog(walaId);

  const data = useMemo<StatusChangeRow[]>(
    () =>
      activityLog?.map((entry) => ({
        timestamp: dayjs(entry.updatedAt).format('YYYY-MM-DD HH:mm:ss'),
        user: entry.updatedByUserName || entry.updatedByEmail,
        status: mapProposedStatus(entry.proposedStatus, entry.status),
      })) || ([] as StatusChangeRow[]),
    [activityLog]
  );

  return (
    <Dialog
      open
      onOpenChange={(open) => {
        if (!open) onClose();
      }}
    >
      <DialogContent
        className={DIALOG_SURFACE}
        showCloseButton={false}
        data-testid="ics204-change-log-dialog"
      >
        <DialogHeader>
          <DialogTitle>Change Log</DialogTitle>
        </DialogHeader>
        <Table className={ICS204_TABLE}>
          <TableHeader>
            <TableRow className="tw:hover:bg-transparent">
              <TableHead className="tw:text-[var(--text-weak)]">
                Status
              </TableHead>
              <TableHead className="tw:text-[var(--text-weak)]">User</TableHead>
              <TableHead className="tw:text-[var(--text-weak)]">
                Timestamp
              </TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {data.length === 0 ? (
              <TableRow>
                <TableCell colSpan={3} className={ICS204_MUTED}>
                  No status changes yet.
                </TableCell>
              </TableRow>
            ) : (
              data.map((row, index) => (
                <TableRow key={`${row.timestamp}-${index}`}>
                  <TableCell className="tw:text-foreground">
                    {row.status}
                  </TableCell>
                  <TableCell className="tw:text-foreground">
                    {row.user}
                  </TableCell>
                  <TableCell className="tw:text-foreground">
                    {row.timestamp}
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
        <DialogFooter>
          <Button
            type="button"
            variant="outline"
            className={ICS204_OUTLINE_BTN}
            onClick={onClose}
          >
            Close
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default ChangeLogDialog;
